import type { ErrorEvent } from "@sentry/react";
import * as Sentry from "@sentry/react";

import { isEvictionClassStorageError, isQuotaExceededError, localStorageCheck } from "@/utils/localStorage";

export type SentryConfig = {
  environment: string;
  dsn: string;
  mode: string;
  release?: string;
};

/**
 * Permanent stages that report to Sentry. `VITE_SENTRY_ENVIRONMENT` passes the deploy stage
 * straight through for non-prod stages, so ephemeral per-ticket stages (e.g. `web-2152`) arrive
 * here under their stage name; an allowlist keeps them (and `local`) from POSTing envelopes to
 * ingest.sentry.io, which surfaced as rejected-request noise during QA (WEB-2162).
 */
const SENTRY_ENABLED_ENVIRONMENTS = new Set(["production", "staging", "dev"]);

/** True when the given `VITE_SENTRY_ENVIRONMENT` value is a permanent stage that sends to Sentry. */
export function isSentryEnabledEnvironment(environment: string | undefined): boolean {
  return environment !== undefined && SENTRY_ENABLED_ENVIRONMENTS.has(environment);
}

/**
 * Sentry is reserved for actual, unrecoverable errors an operator must look at — error screens,
 * data loss, exhausted recovery. Transient and recoverable signals (retried requests, storage
 * recovery, slow probes, network blips) ship to Honeycomb as `reliability` telemetry events via
 * `@/services/telemetry` instead (Grafana once web metrics are wired up there). Performance
 * measurement lives entirely in the OTel/Honeycomb pipeline, so Sentry tracing/profiling stays
 * off — no span-derived issues (N+1 API call, degraded UI performance) get created.
 */
export function initializeSentry(config: SentryConfig): void {
  Sentry.init({
    enabled: isSentryEnabledEnvironment(config.environment),
    dsn: config.dsn,
    environment: config.environment ?? config.mode,
    release: config.release,
    integrations: [
      // Replays stay on: an error-linked replay is the single best triage context for web.
      Sentry.replayIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
    ],
    ignoreErrors: [
      "Failed to connect to server after exhausting retry policy.",
      "Failed to update a ServiceWorker for scope",
      "b.match is not a function",
      "gtm|gtag",
      // Benign HTMLMediaElement race: pause()/load() called before play() promise resolves.
      "The play() request was interrupted",
    ],
    enableLogs: true,

    beforeSend(event, hint) {
      if (isQuotaExceededError(hint.originalException)) {
        localStorageCheck();
        event.level = "warning";
        event.fingerprint = ["quota-exceeded-error"];
      } else if (isEvictionClassStorageError(hint.originalException)) {
        // Same treatment as quota: a browser that evicted or broke its own backing store is an
        // environment condition, not an application defect — downgrade and group, don't page
        // (WEB-2248).
        event.level = "warning";
        event.fingerprint = ["storage-eviction-error"];
      }
      // WEB-2068: Chrome's "Failed to fetch" and Firefox's "NetworkError when
      // attempting to fetch resource." indicate the browser could not reach the
      // API before receiving any response (user offline, DNS failure, TLS error,
      // connection refused). These are not application defects; API availability
      // is monitored server-side via CloudWatch. Drop them.
      if (isTransientNetworkFetchRejection(event)) {
        return null;
      }
      // Our own transport throws an ApiError for any 5xx (apiv2 500s carry the body's literal
      // "Internal Server Error"; proxy-layer 5xx fall back to "Request failed with status NNN").
      // The backend already pages on its own 5xx via apiv2's error middleware, so the *unhandled*
      // client-side copies are duplicate noise (the request-layer OTel span records the failure to
      // Honeycomb). Only the unhandled mechanism is dropped — an explicit capture that carries a
      // 5xx as its cause (e.g. the `app_boot` capture behind the StorageError screen) must still
      // page.
      if (isUnhandledInternalServerErrorRejection(event)) {
        return null;
      }
      return event;
    },

    // Every error-carrying session gets a replay; ambient session sampling is off — perceived
    // performance is measured through the OTel/Honeycomb pipeline, not Sentry.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
}

/**
 * Detects transient browser network errors from `fetch()` calls where the request fails before
 * receiving any response — user offline, DNS failure, TLS error, or connection refused.
 *
 * Chrome surfaces these as `TypeError: Failed to fetch`; Firefox as
 * `TypeError: NetworkError when attempting to fetch resource.`. The API transport wraps the same
 * failures in an `ApiError` (status 0) that keeps the browser's message. Neither indicates an
 * application defect — API availability is monitored server-side via CloudWatch, not via browser
 * Sentry events.
 */
export function isTransientNetworkFetchRejection(event: ErrorEvent): boolean {
  const exception = event.exception?.values?.[0];
  if (!exception) return false;
  if (exception.type !== "TypeError" && exception.type !== "ApiError") return false;

  const message = exception.value ?? "";
  return message === "Failed to fetch" || message === "NetworkError when attempting to fetch resource.";
}

/**
 * Detects the *unhandled* rejection copies of the transport's 5xx `ApiError`s.
 *
 * apiv2 500s carry the response body's literal "Internal Server Error"; 5xx from the proxy layers
 * in front of it (ALB, CloudFront) surface as the transport's "Request failed with status 5xx"
 * fallback. The backend pages on its own 5xx, so these duplicates are dropped — but only when the
 * mechanism is an unhandled rejection / onerror. Explicit `captureException` calls (mechanism
 * `generic`, `handled: true`) that carry the same message as a cause must still page;
 * `ignoreErrors` can't make that distinction, hence this filter.
 */
export function isUnhandledInternalServerErrorRejection(event: ErrorEvent): boolean {
  const exception = event.exception?.values?.[0];
  if (!exception) return false;

  const message = exception.value ?? "";
  const isApiServerError =
    message === "Internal Server Error" ||
    (exception.type === "ApiError" && /^Request failed with status 5\d\d\b/.test(message));
  if (!isApiServerError) return false;

  const mechanism = exception.mechanism;
  return mechanism?.handled === false || mechanism?.type === "onunhandledrejection";
}
